import { useCallback, useContext, useEffect, useState } from "react";
import CompanyService from "../../services/CompanyService";
import UserService from "../../services/UserService";
import ModalService from "../shared/bootstrap/modal/Modal";
import ToastContext from "../shared/bootstrap/Toast";
import Dropdown from "../shared/components/Dropdown";
import FloatingPanelService from "../shared/FloatingPanel";
import { TranslationService } from "../../services/TranslationService";
import { UserListGetResponse } from "./entities/UserListGetResponse";
import { UserEdit } from "./UserEdit";
import Table, { TableHeader } from "../shared/Table";
import UserTableProvider from "./UserTableProvider";
import TableContext from "../task/TableContext";
import { DefaultModalProps } from "../shared/bootstrap/DefaultModal";
import TooltipComponent from "../shared/TooltipComponent";
import SearchInput from "../shared/components/SearchInput";
import { ButtonTooltipIcon } from "../shared/components/ButtonTooltipIcon";
import RoleService, { RoleGetListRequest } from "../../services/RoleService";

const btnEditUserClick = async (
  response: UserListGetResponse | undefined,
  reload: () => void,
  title: string,
  roles?: any[],
  userId?: number,
  readonly?: boolean
) => {
  FloatingPanelService.showPanel({
    title: title,
    children: (
      <UserEdit
        userId={userId}
        panelOptions={
          response?.panels.map((panel) => ({
            value: panel.DashboardPanelID,
            text: panel?.Name
          })) ?? []
        }
        onSubmit={reload}
        readonly={readonly}
        userByList={response?.users.find((el) => el.UserID === userId)}
        roles={roles}
      />
    ),
    width: 900,
    height: 669
  });
};

const UserList = () => {
  const [roles, setRoles] = useState([]);
  const { translate } = TranslationService;

  const tableHeaders: TableHeader[] = [
    new TableHeader("Name", translate?.Name, false, false),
    new TableHeader("Email", translate.Email, false, false),
    new TableHeader("Profile", translate.Profile, false, false),
    ...(CompanyService.canShowCashApp()
      ? [
          new TableHeader(
            "ProfileCashapp",
            `${translate.Profile} ${translate.Cashapp}`,
            false,
            false
          )
        ]
      : []),
    new TableHeader("Panel", translate.Panel, false, false),
    new TableHeader("LastLogin", translate.LastLogin, false, false),
    new TableHeader("Roles", translate.Roles, false, false),
    new TableHeader("Actions", "", false, false)
  ];

  useEffect(() => {
    const loadRoleList = async () => {
      const result = await RoleService.getList(new RoleGetListRequest());
      if (result instanceof Error) {
        return result;
      }
      setRoles(result.list);
    };

    loadRoleList();
  }, []);

  return (
    <UserTableProvider>
      <div className="main-layout-container mx-3">
        <div className="main-panel">
          <h4 className="mb-2">{TranslationService.translate.Users}</h4>
          <div className="card">
            <UserCount />
            <UserFilters roles={roles} />
            <Table
              emptyMessage={translate.NoResults}
              headers={tableHeaders}
              item={({ data }) => UserTableItem({ data, roles })}
            />
          </div>
        </div>
      </div>
    </UserTableProvider>
  );
};

export const UserCount = () => {
  const context = useContext(TableContext);
  const users = context.response?.result.users;

  const managementUsersCount =
    users?.filter((x: UserListGetResponse.User) => !x.ReadOnly).length ?? 0;
  const managementUser = TranslationService.translate[
    managementUsersCount > 1 ? "ManagementUsers" : "ManagementUser"
  ].replace("{1}", managementUsersCount);

  const queryUsersCount =
    users?.filter((x: UserListGetResponse.User) => x.ReadOnly).length ?? 0;
  const queryUsers = TranslationService.translate[
    queryUsersCount > 1 ? "QueryUsers" : "QueryUser1"
  ].replace("{1}", queryUsersCount);

  return <div className="mb-2">{`${managementUser} + ${queryUsers}`}</div>;
};

export const UserFilters = ({ roles }: { roles: any[] }) => {
  const context = useContext(TableContext);
  const profileOptionList = [
    "AllProfiles",
    "Guest",
    "Readonly",
    "Administrator",
    "Creator"
  ].map((x) => ({
    value: x,
    text: TranslationService.getTranslation("UserRole" + x)
  }));

  const filterList = (profile?: string) => {
    context.applyFilters({ newProfile: profile });
  };

  const searchTerm = (value: string) => {
    context.applyFilters({ searchTerm: value });
  };

  const handleDownload = useCallback(async () => {
    await UserService.downloadFile();
  }, []);

  return (
    <div className="genericHeader">
      <div className="w-100 ps-0">
        <div className="col-auto ps-0">
          <div className="actionButtonsHeader">
            {!context.loading && CompanyService.canDo("edituser") ? (
              <ButtonTooltipIcon
                withTitle
                title={TranslationService.translate.New}
                icon="fa-light fa-plus"
                onClick={() =>
                  btnEditUserClick(
                    context.response.result,
                    context.reload,
                    TranslationService.translate.NewUser,
                    roles
                  )
                }
                isLink={false}
              />
            ) : null}
            <ButtonTooltipIcon
              title={TranslationService.translate.Export}
              icon="fa-light fa-arrow-down-to-line"
              onClick={handleDownload}
              isLink={false}
            />

            <Dropdown onChange={filterList} items={profileOptionList} />
          </div>
        </div>
      </div>
      <SearchInput onSearch={searchTerm} autosearch={true} />
    </div>
  );
};

export const UserTableItem = ({
  data,
  roles
}: {
  data: UserListGetResponse.User;
  roles: any[];
}) => {
  const context = useContext(TableContext);
  const { showDefaultModal } = ModalService;
  const { showToast } = useContext(ToastContext);

  const btnDeleteUserClick = async (userId: number) => {
    const modalProps: DefaultModalProps = {
      message: TranslationService.translate.ConfirmDeleteText,
      acceptButtonLabel: TranslationService.translate.Accept,
      onAcceptClick: async () => {
        const result = await UserService.delete(userId);
        if (result instanceof Error) {
          showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          showToast(
            TranslationService.translate.UserDeleted,
            undefined,
            "success"
          );
          ModalService.hideModal();
          context.reload();
        }
      }
    };
    showDefaultModal(modalProps);
  };

  const getLastLoginText = (dateLastLoginStr: string | undefined | null) => {
    if (!dateLastLoginStr) {
      return "-";
    }
    const dateLastLogin = new Date(dateLastLoginStr);
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const dateNow = new Date();

    const diffDays = Math.round(
      Math.abs((dateNow.getTime() - dateLastLogin.getTime()) / oneDay)
    );
    return diffDays === 0
      ? TranslationService.translate.Today
      : TranslationService.translate.NDaysAgo.replace(
          "{days}",
          diffDays.toString()
        );
  };
  const rolesbyUser = data.roles
    ?.map((ur) => roles.find((r) => r.RoleID === ur)?.Name)
    .join(", ");
  return (
    <tr key={data.UserID} className="show-child-on-hover">
      <td>
        {data.HasPassword ? (
          data.UserName
        ) : (
          <i>{TranslationService.translate.Pending}</i>
        )}
      </td>
      <td>{data.UserEmail}</td>
      <td
        className={
          UserService.getRole(data) === "Creator" ? "fw-bolder" : undefined
        }
      >
        {TranslationService.getTranslation(
          "UserRole" + UserService.getRole(data)
        )}
      </td>
      {CompanyService.canShowCashApp() && (
        <td>
          {data.CashApp
            ? TranslationService.getTranslation(
                "UserRole" +
                  (data.AdminCashApp
                    ? "Administrator"
                    : data.ReadOnlyCashApp
                      ? "Readonly"
                      : "Guest")
              )
            : "-"}
        </td>
      )}
      <td>
        {data.DashboardPanelID
          ? context.response!.result.panels.find(
              (y: UserListGetResponse.Panel) =>
                y.DashboardPanelID === data.DashboardPanelID
            )?.Name
          : TranslationService.translate.Standard}
      </td>
      <td>{getLastLoginText(data.LastLogin)}</td>
      <TooltipComponent title={rolesbyUser} placement="top">
        <td>{rolesbyUser}</td>
      </TooltipComponent>
      <td>
        <TooltipComponent
          title={TranslationService.translate.Edit}
          placement="right"
          className="align-middle"
        >
          <i
            className={
              "far fa-fw pointer fa-eye mx-2 show-when-hovering-parent"
            }
            onClick={() =>
              btnEditUserClick(
                context.response.result,
                context.reload,
                TranslationService.translate.User + " " + data.UserName,
                roles,
                data.UserID,
                true
              )
            }
          />
        </TooltipComponent>
        {CompanyService.canDo("edituser") && (
          <TooltipComponent
            title={TranslationService.translate.Edit}
            placement="right"
            className="align-middle"
          >
            <i
              className={
                "far fa-fw pointer fa-pen mx-2 show-when-hovering-parent"
              }
              onClick={() =>
                btnEditUserClick(
                  context.response.result,
                  context.reload,
                  TranslationService.translate.User + " " + data.UserName,
                  roles,
                  data.UserID,
                  false
                )
              }
            />
          </TooltipComponent>
        )}
        {CompanyService.canDo("edituser") && (
          <TooltipComponent
            title={TranslationService.translate.Delete}
            placement="right"
            className="align-middle"
          >
            <i
              className={
                UserService.getRole(data) === "Creator" ||
                data.UserID.toString() === CompanyService.getUserid()
                  ? "d-none"
                  : "far fa-fw pointer fa-trash text-danger mx-2 show-when-hovering-parent"
              }
              onClick={() => btnDeleteUserClick(data.UserID)}
            />
          </TooltipComponent>
        )}
      </td>
    </tr>
  );
};

export default UserList;
